/**
 * Routing-graph helpers for lesson-pack editing.
 *
 * Electron walks a pack via `beatMeta.next.default` (beat ids), not by
 * iterating `slides[]` — so structural edits (insert / remove) must rewire
 * the routing graph in the same PATCH /ops batch they emit the structural
 * op. These helpers are the small, pure primitives those flows use.
 *
 * See backend docs:
 *   - INTEGRATION.md §4 (op shapes)
 *   - SCHEMA_USAGE.md §"Editor structural-change rules" (Rules 1 & 2)
 */

import type { BeatMeta, Slide } from './schema.types';

// ---------- helpers ----------

/** Parse "slide-XYZ" → "XYZ". Returns null if the slide id doesn't carry a
 *  beat id (i.e. an old script-shaped slide or a malformed pack slide). */
export function beatIdOf(slide: Slide): string | null {
  return slide.beatMeta?.beatId ?? parseBeatId(slide.id);
}

function parseBeatId(slideId: string): string | null {
  return slideId.startsWith('slide-') ? slideId.slice('slide-'.length) : null;
}

/** Build an `slide-${beatId}` slide id with the convention SCHEMA_USAGE.md
 *  documents. */
export function slideIdFor(beatId: string): string {
  return `slide-${beatId}`;
}

/** Lookup map: beatId → slide. Skips slides with no beat id. */
function indexByBeat(slides: Slide[]): Map<string, Slide> {
  const out = new Map<string, Slide>();
  for (const s of slides) {
    const b = beatIdOf(s);
    if (b) out.set(b, s);
  }
  return out;
}

// ---------- insertion ----------

/**
 * Resolve the routing-graph predecessor and successor for inserting a new
 * slide at a given array position.
 *
 * - `arrayIndex >= slides.length` (or undefined) → "append at the end of the
 *   lesson". Predecessor = current terminal slide (the one whose
 *   `next.default === null`); successor = `null`.
 * - Otherwise → the slide currently at `arrayIndex` becomes the new slide's
 *   *routing* successor; predecessor = whichever slide currently points its
 *   `next.default` at that successor (NOT `slides[arrayIndex - 1]`).
 *
 * Returns `predecessor: null` when no rewire is possible (the pack has no
 * beatMeta, or we're inserting before the entry beat). Callers should still
 * emit the `insertSlide` op; they just can't emit the `updateRouting`.
 */
export function findInsertionEdge(
  slides: Slide[],
  arrayIndex?: number,
): {
  predecessor: Slide | null;
  successorBeatId: string | null;
} {
  if (slides.length === 0) return { predecessor: null, successorBeatId: null };

  // Append at the end → predecessor is the terminal slide.
  if (typeof arrayIndex !== 'number' || arrayIndex >= slides.length) {
    const terminal = slides.find((s) => s.beatMeta?.next?.default == null);
    return {
      predecessor: terminal ?? null,
      successorBeatId: null,
    };
  }

  // Middle insertion: the slide at `arrayIndex` becomes the routing successor.
  const successor = slides[Math.max(0, arrayIndex)];
  const successorBeatId = beatIdOf(successor);
  if (!successorBeatId) return { predecessor: null, successorBeatId: null };

  const predecessor =
    slides.find((s) => s !== successor && s.beatMeta?.next?.default === successorBeatId) ?? null;
  return { predecessor, successorBeatId };
}

// ---------- removal (used by PR 3) ----------

/** Every routing pointer on every other slide that names `doomedBeatId`. */
export interface PointerHit {
  /** The slide that holds the pointer (will need its beatMeta rewritten). */
  slideId: string;
  /** Dotted path describing which field needs to change. */
  location:
    | 'next.default'
    | 'next.ifExpanded'
    | 'next.ifReteach'
    | 'next.ifSkipped'
    | 'adaptiveTriggers.lowEngagement.targetBeatId'
    | 'adaptiveTriggers.highConfusion.targetBeatId'
    | 'adaptiveTriggers.lowConfidence.targetBeatId'
    | 'onMajorityYes.targetBeatId'
    | 'onMajorityNo.targetBeatId'
    | 'onSplit.targetBeatId'
    | 'onHighConfidence.targetBeatId'
    | 'onMediumConfidence.targetBeatId'
    | 'onLowConfidence.targetBeatId';
}

const NEXT_KEYS = ['default', 'ifExpanded', 'ifReteach', 'ifSkipped'] as const;
const TRIGGER_KEYS = ['lowEngagement', 'highConfusion', 'lowConfidence'] as const;
const PACE_KEYS = ['onMajorityYes', 'onMajorityNo', 'onSplit'] as const;
const CONFIDENCE_KEYS = [
  'onHighConfidence',
  'onMediumConfidence',
  'onLowConfidence',
] as const;

/** Scan every slide (other than the doomed one) for pointers that name the
 *  doomed beat id. Used by `removeSlide` to know which `updateRouting` /
 *  `updateBeatMeta` ops to batch before the actual `removeSlide`. */
export function findPointersTo(slides: Slide[], doomedBeatId: string): PointerHit[] {
  const out: PointerHit[] = [];
  for (const s of slides) {
    if (beatIdOf(s) === doomedBeatId) continue;
    const bm = s.beatMeta;
    if (!bm) continue;

    // next.{default,ifExpanded,ifReteach,ifSkipped}
    for (const k of NEXT_KEYS) {
      if (bm.next?.[k] === doomedBeatId) {
        out.push({ slideId: s.id, location: `next.${k}` as PointerHit['location'] });
      }
    }

    // adaptiveTriggers.<key>.targetBeatId
    for (const k of TRIGGER_KEYS) {
      const trig = (bm.adaptiveTriggers as Record<string, { targetBeatId?: string }> | undefined)?.[k];
      if (trig?.targetBeatId === doomedBeatId) {
        out.push({
          slideId: s.id,
          location: `adaptiveTriggers.${k}.targetBeatId` as PointerHit['location'],
        });
      }
    }

    // pace_check action.targetBeatId
    for (const k of PACE_KEYS) {
      const action = (bm as unknown as Record<string, { targetBeatId?: string }>)[k];
      if (action?.targetBeatId === doomedBeatId) {
        out.push({
          slideId: s.id,
          location: `${k}.targetBeatId` as PointerHit['location'],
        });
      }
    }

    // confidence_check action.targetBeatId
    for (const k of CONFIDENCE_KEYS) {
      const action = (bm as unknown as Record<string, { targetBeatId?: string }>)[k];
      if (action?.targetBeatId === doomedBeatId) {
        out.push({
          slideId: s.id,
          location: `${k}.targetBeatId` as PointerHit['location'],
        });
      }
    }
  }
  return out;
}

// ---------- diagnostics ----------

/** Find slides that no other slide's routing references (and that aren't
 *  the entry beat). Used by pre-save validation. */
export function findOrphanSlides(
  slides: Slide[],
  entryBeatId: string | null,
): Slide[] {
  const inbound = new Set<string>();
  for (const s of slides) {
    const bm = s.beatMeta;
    if (!bm) continue;
    for (const k of NEXT_KEYS) {
      const v = bm.next?.[k];
      if (v) inbound.add(v);
    }
    for (const k of TRIGGER_KEYS) {
      const v = (bm.adaptiveTriggers as Record<string, { targetBeatId?: string }> | undefined)?.[k]
        ?.targetBeatId;
      if (v) inbound.add(v);
    }
  }
  return slides.filter((s) => {
    const b = beatIdOf(s);
    if (!b) return false;
    if (entryBeatId && b === entryBeatId) return false;
    return !inbound.has(b);
  });
}

// ---------- beatMeta scaffolding ----------

/** Build a fresh, complete `beatMeta` scaffold for a new pack slide. The
 *  shape matches SCHEMA_USAGE.md Rule 1 ("Inserting a new slide"). For
 *  `kind: 'quiz'` the caller is expected to overlay quiz-specific fields
 *  (`correctOptionId`, `branches`, `thresholds`) before insertion — this
 *  helper only sets the common envelope. */
export function blankBeatMeta(beatId: string, kind: BeatMeta['kind']): BeatMeta {
  return {
    beatId,
    slideId: slideIdFor(beatId),
    kind,
    cognitiveLoad: 'medium',
    introduces: [],
    uses: [],
    requires: [],
    estimatedDurationSec: 60,
    teacherNotes: {},
    next: {
      default: null as unknown as string | undefined,
      ifExpanded: null as unknown as string | undefined,
      ifReteach: null as unknown as string | undefined,
      ifSkipped: null as unknown as string | undefined,
    },
    adaptiveTriggers: {
      lowEngagement: { action: 'continue' },
      highConfusion: { action: 'continue' },
      lowConfidence: { action: 'continue' },
    },
    expandedSegments: [],
    anticipatedDoubts: [],
  };
}
